/**
 * Library-free i18n shim for the wizard "Deployment scope" summary row.
 *
 * Mirrors the pattern in `unsavedChangesStrings.ts` so that, when the app
 * eventually adopts `react-i18next` (or similar), the migration is a
 * mechanical find-and-replace at the call sites.
 *
 * Why a separate registry per surface?
 *  - Keeps the key namespaces small and obviously-typed (no
 *    `"common.foo.bar"` string soup).
 *  - Lets product teams audit / hand off translations one feature at a time.
 *  - Matches the existing `tUnsavedChanges` ergonomics so contributors don't
 *    have to learn a new pattern.
 *
 * ## Pluralization
 *
 * The "device" noun is resolved through {@link pluralizeDeviceNoun}, which
 * delegates to the platform's `Intl.PluralRules` for the active locale (set
 * via {@link configureDeploymentScopeLocale}). Translators can supply a value
 * for any subset of the six CLDR plural categories — `deviceZero`,
 * `deviceOne`, `deviceTwo`, `deviceFew`, `deviceMany`, `deviceOther` — and
 * the helper falls back through a sensible chain that ends at the legacy
 * English-style `deviceSingular` / `devicePlural` pair, then the built-in
 * defaults. This keeps existing English-only callers working unchanged while
 * letting locales like Russian (one/few/many/other), Arabic (zero/one/two/
 * few/many/other), or Welsh (zero/one/two/few/many/other) render with
 * grammatically correct nouns instead of the naïve `count === 1` split.
 */

export type DeploymentScopeStringKey =
  | "followsApp"
  | "selectTagsPrompt"
  | "tagsSelectedSummary" // {count} tags selected
  | "deviceSingular" // device — legacy English-style fallback
  | "devicePlural" // devices — legacy English-style fallback
  // CLDR plural-category nouns — translators fill the ones their language uses.
  | "deviceZero"
  | "deviceOne"
  | "deviceTwo"
  | "deviceFew"
  | "deviceMany"
  | "deviceOther"
  | "exactDeviceSuffix" // {count} {noun}
  | "upToDeviceSuffix" // up to {count} {noun}
  | "multiTagUpperBoundHint" // helper text for 2+ tag selections
  | "calculatingDevices" // loading placeholder while counts are being fetched
  | "cachedCountsBadge" // shown when counts came from react-query cache (no fresh fetch)
  | "deviceCountTooltipLabel" // a11y label for the info tooltip trigger
  | "deviceCountTooltipSingleTag" // tooltip body when exactly one tag is selected
  | "deviceCountTooltipMultiTag" // tooltip body when 2+ tags are selected
  | "deviceCountTooltipCatalog"; // tooltip body for catalog targets ("Follows the app")

/**
 * English defaults. Keep these in sync with the placeholders documented in
 * the README; values are passed through {@link interpolate} so any literal
 * `{count}` / `{noun}` / `{names}` token is replaced at format time.
 *
 * The CLDR-keyed device nouns are intentionally left blank in English so the
 * resolver falls through to the legacy `deviceSingular` / `devicePlural`
 * pair — that keeps existing snapshots stable and avoids forcing translators
 * to mirror values they didn't supply.
 */
export const DEFAULT_DEPLOYMENT_SCOPE_STRINGS: Record<
  DeploymentScopeStringKey,
  string
> = {
  followsApp: "Follows the app",
  selectTagsPrompt: "Select one or more tags below",
  tagsSelectedSummary: "{count} tags selected",
  deviceSingular: "device",
  devicePlural: "devices",
  deviceZero: "",
  deviceOne: "",
  deviceTwo: "",
  deviceFew: "",
  deviceMany: "",
  deviceOther: "",
  exactDeviceSuffix: "{count} {noun}",
  upToDeviceSuffix: "up to {count} {noun}",
  multiTagUpperBoundHint:
    "Upper bound — devices that belong to multiple selected tags are counted once per tag.",
  calculatingDevices: "Calculating devices…",
  cachedCountsBadge: "Using cached counts",
  deviceCountTooltipLabel: "How is this number computed?",
  deviceCountTooltipSingleTag:
    "Exact count: the number of devices currently matching the selected tag.",
  deviceCountTooltipMultiTag:
    "Upper bound: we sum each tag's device count. Devices that belong to more than one selected tag are counted once per tag, so the true reach may be lower.",
  deviceCountTooltipCatalog:
    "This configuration's deployment follows the parent app — devices receive it wherever the app is installed.",
};

const overrides: Partial<Record<DeploymentScopeStringKey, string>> = {};

/**
 * Hard cap on any single localized string — same rationale as the
 * unsaved-changes registry: untrusted JSON bundles occasionally ship
 * pathologically long values that would blow out summary-row layout.
 */
export const DEPLOYMENT_SCOPE_MAX_STRING_LENGTH = 500;

function isAcceptableOverrideValue(value: unknown): value is string {
  return (
    typeof value === "string" &&
    value.length > 0 &&
    value.length <= DEPLOYMENT_SCOPE_MAX_STRING_LENGTH
  );
}

/**
 * Bulk-set localized strings, typically once at app bootstrap when the
 * active locale is known. Keys you omit fall back to the English defaults.
 */
export function configureDeploymentScopeStrings(
  next: Partial<Record<DeploymentScopeStringKey, string>>,
): void {
  for (const k of Object.keys(next) as DeploymentScopeStringKey[]) {
    const v = next[k];
    if (isAcceptableOverrideValue(v)) overrides[k] = v;
  }
}

/** Resets all overrides — intended for tests / locale switches. */
export function resetDeploymentScopeStrings(): void {
  for (const k of Object.keys(overrides)) {
    delete overrides[k as DeploymentScopeStringKey];
  }
  // Also clear any locale override so tests start from the platform default.
  activeLocale = undefined;
  cachedPluralRules = undefined;
}

/**
 * Returned when a caller asks for a key that isn't in the defaults map and
 * no override supplies it. Empty string keeps direct-render call sites
 * from accidentally showing the literal text "undefined".
 */
export const DEPLOYMENT_SCOPE_MISSING_KEY_FALLBACK = "";

/**
 * Replace `{name}` tokens in `template` with values from `vars`. Missing
 * variables are left as the literal token so a translator's typo doesn't
 * silently render as `undefined`. Numbers are coerced via `String(...)`.
 */
function interpolate(
  template: string,
  vars?: Record<string, string | number>,
): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name) => {
    const v = vars[name];
    return v === undefined ? match : String(v);
  });
}

/**
 * Resolve a deployment-scope string by key, with optional `{name}` token
 * interpolation. Resolution priority (highest first):
 *   1. Global overrides set via {@link configureDeploymentScopeStrings}
 *   2. English defaults in {@link DEFAULT_DEPLOYMENT_SCOPE_STRINGS}
 *   3. {@link DEPLOYMENT_SCOPE_MISSING_KEY_FALLBACK} (empty string)
 */
export function tDeploymentScope(
  key: DeploymentScopeStringKey,
  vars?: Record<string, string | number>,
): string {
  const overrideValue = overrides[key];
  if (typeof overrideValue === "string") {
    return interpolate(overrideValue, vars);
  }
  const defaultValue = DEFAULT_DEPLOYMENT_SCOPE_STRINGS[key];
  if (typeof defaultValue === "string") return interpolate(defaultValue, vars);

  if (
    typeof process !== "undefined" &&
    process.env?.NODE_ENV !== "production"
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      `[deploymentScopeStrings] Unknown key "${String(
        key,
      )}" — returning empty string. Add it to DEFAULT_DEPLOYMENT_SCOPE_STRINGS.`,
    );
  }
  return DEPLOYMENT_SCOPE_MISSING_KEY_FALLBACK;
}

// ---------------------------------------------------------------------------
// Locale-aware pluralization
// ---------------------------------------------------------------------------

/**
 * Active BCP-47 locale tag used for plural-category resolution. `undefined`
 * means "let `Intl.PluralRules` pick the runtime default", which matches
 * behavior prior to this feature for English-only deployments.
 */
let activeLocale: string | undefined;

/**
 * Memoized `Intl.PluralRules` instance for {@link activeLocale}. Recomputed
 * lazily whenever the locale changes (or after a registry reset).
 */
let cachedPluralRules: Intl.PluralRules | undefined;

/**
 * Set the active locale used by {@link pluralizeDeviceNoun}. Pass `undefined`
 * to clear the override and fall back to the platform default. Invalid tags
 * are ignored — `Intl.PluralRules` would otherwise throw at call time and
 * crash the row render.
 */
export function configureDeploymentScopeLocale(
  locale: string | undefined,
): void {
  if (locale === undefined) {
    activeLocale = undefined;
    cachedPluralRules = undefined;
    return;
  }
  if (typeof locale !== "string" || locale.trim() === "") return;
  try {
    // Probe construction — throws RangeError for malformed tags.
    cachedPluralRules = new Intl.PluralRules(locale);
    activeLocale = locale;
  } catch {
    // Keep the previous (valid) locale — never propagate an i18n config error
    // to the caller; a slightly-wrong noun is preferable to a thrown render.
  }
}

/** Read the currently-active locale, or `undefined` if none was configured. */
export function getDeploymentScopeLocale(): string | undefined {
  return activeLocale;
}

function getPluralRules(): Intl.PluralRules | undefined {
  if (cachedPluralRules) return cachedPluralRules;
  try {
    cachedPluralRules = new Intl.PluralRules(activeLocale);
    return cachedPluralRules;
  } catch {
    // Environment without `Intl.PluralRules` (extremely old runtimes, some
    // sandboxed JS engines). Caller will fall back to the legacy
    // singular/plural pair below.
    return undefined;
  }
}

/**
 * CLDR plural categories, in the fallback order applied when a locale's
 * resolved category has no override and no built-in default. The chain
 * mirrors CLDR's "closest neighbor" recommendation — e.g., a missing `few`
 * falls back to `many`, then `other`. `other` is the universal default and
 * is always populated (via `devicePlural`).
 */
const PLURAL_FALLBACK_CHAIN: Record<
  Intl.LDMLPluralRule,
  DeploymentScopeStringKey[]
> = {
  zero: ["deviceZero", "deviceOther", "devicePlural"],
  one: ["deviceOne", "deviceSingular"],
  two: ["deviceTwo", "deviceOther", "devicePlural"],
  few: ["deviceFew", "deviceMany", "deviceOther", "devicePlural"],
  many: ["deviceMany", "deviceOther", "devicePlural"],
  other: ["deviceOther", "devicePlural"],
};

/**
 * Resolve the device noun for `count` under the active locale. Walks the
 * fallback chain for the locale's CLDR category and returns the first
 * non-empty value. Always succeeds — the worst case returns the English
 * `"devices"` default.
 *
 * Examples:
 *  - en, count=1 → `"device"` (one → deviceOne empty → deviceSingular)
 *  - en, count=2 → `"devices"` (other → deviceOther empty → devicePlural)
 *  - ru, count=2, with `{deviceFew: "устройства", deviceMany: "устройств"}`
 *    → `"устройства"` (Russian "few" covers 2–4)
 *  - ar, count=0, with `{deviceZero: "جهاز"}` → `"جهاز"`
 */
export function pluralizeDeviceNoun(count: number): string {
  const rules = getPluralRules();
  // Without `Intl.PluralRules`, fall back to the historical English split.
  const category: Intl.LDMLPluralRule = rules
    ? rules.select(count)
    : count === 1
      ? "one"
      : "other";

  const chain = PLURAL_FALLBACK_CHAIN[category] ?? PLURAL_FALLBACK_CHAIN.other;
  for (const key of chain) {
    const value = tDeploymentScope(key);
    if (value) return value;
  }
  // All chain entries empty — extremely unusual since `devicePlural` ships a
  // default. Return the missing-key fallback rather than throwing.
  return DEPLOYMENT_SCOPE_MISSING_KEY_FALLBACK;
}
